import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../../api';
import { User } from '@/shared/types';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  errorMessage: { title: string; description?: string } | null;
  successMessage: string | null;
  setErrorMessage: (message: { title: string; description?: string } | null) => void;
  setSuccessMessage: (message: string | null) => void;
  setIsLoading: (loading: boolean) => void;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, username?: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Utility function to check if JWT token is expired
const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    console.error('Error parsing token:', error);
    return true; // Consider invalid tokens as expired
  }
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<{ title: string; description?: string } | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setUser(null);
        setIsLoading(false);
        return;
      }

      // Check if token is expired before making API call
      if (isTokenExpired(token)) {
        console.log('Token is expired, clearing auth state');
        localStorage.removeItem('authToken');
        setUser(null);
        setIsLoading(false);
        return;
      }

      const response = await api.get('/auth/me');
      setUser(response.data.data.user);
    } catch (error: any) {
      console.error('Auth check failed:', error);
      // Only clear token and user if it's an authentication error
      if (error.response?.status === 401) {
        console.log('Token expired or invalid, clearing auth state');
        localStorage.removeItem('authToken');
        setUser(null);
      }
      // Don't clear token for network errors or other issues
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const data = response.data;

      localStorage.setItem('authToken', data.data.token);
      setUser(data.data.user);
      setSuccessMessage('Login successful!');
    } catch (error: any) {
      const errorCode = error.response?.data?.code;
      let errorMessage = error.response?.data?.error || error.message || 'Login failed';
      
      // Handle specific email verification error
      if (errorCode === 'EMAIL_NOT_VERIFIED') {
        setErrorMessage({
          title: 'Email Not Verified',
          description:
            'Check your inbox for a message from confirmation@pricingtable.ai and click the verification link before signing in. You can request a new link from the email verification page.',
        });
      } else {
        setErrorMessage({
          title: 'Login failed',
          description: errorMessage,
        });
      }
      throw error;
    }
  };

  const signup = async (email: string, password: string) => {
    try {
      const response = await api.post('/auth/register', { email, password });
      const data = response.data;

      // Don't set token or user if registration doesn't return a token (email verification required)
      if (data.data.token) {
        localStorage.setItem('authToken', data.data.token);
        setUser(data.data.user);
        setSuccessMessage('Registration successful!');
      } else {
        // Email verification required
        setSuccessMessage('Registration successful! Please check your email and click the verification link to complete your account setup.');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Registration failed';
      setErrorMessage({
        title: 'Registration failed',
        description: errorMessage,
      });
      throw error;
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        await api.post('/auth/logout');
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('authToken');
      setUser(null);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // Set up periodic token validation (every 10 minutes)
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      checkAuth();
    }, 10 * 60 * 1000); // 10 minutes

    return () => clearInterval(interval);
  }, [user]);

  // Set up visibility change listener to check auth when user returns to tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        // Check auth when user returns to the tab
        checkAuth();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]);

  // Set up activity tracking to extend session
  useEffect(() => {
    if (!user) return;

    let activityTimeout: NodeJS.Timeout;
    
    const resetActivityTimeout = () => {
      clearTimeout(activityTimeout);
      // Set timeout to 30 minutes of inactivity
      activityTimeout = setTimeout(() => {
        console.log('User inactive for 30 minutes, checking auth...');
        checkAuth();
      }, 30 * 60 * 1000);
    };

    // Reset timeout on user activity
    const handleActivity = () => {
      resetActivityTimeout();
    };

    // Listen for user activity
    ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'].forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Initial timeout
    resetActivityTimeout();

    return () => {
      clearTimeout(activityTimeout);
      ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'].forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [user]);

  const value: AuthContextType = {
    user,
    isLoading,
    errorMessage,
    successMessage,
    setErrorMessage,
    setSuccessMessage,
    setIsLoading,
    login,
    signup,
    logout,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
